import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type AdminUserDetail,
  type AdminUserInvitePayload,
  type AdminUserListParams,
  type AdminUserListResponse,
  type AdminUserUpdatePayload,
  deleteAdminUser,
  forceAdminUserEmailVerified,
  getAdminUser,
  inviteAdminUser,
  listAdminUsers,
  resendAdminInvite,
  setAdminUserBlocked,
  updateAdminUser,
} from '../../api/adminUsers';
import { useNotification } from '../../context/NotificationProvider';
import { notifyMutationError } from '../../lib/errors';
import { queryKeys } from '../../lib/pwa/queryConfig';

function patchListsWithUpdatedUser(
  queryClient: ReturnType<typeof useQueryClient>,
  updated: AdminUserDetail,
): void {
  queryClient.setQueriesData<AdminUserListResponse>(
    { queryKey: queryKeys.admin.users.all },
    (old) => {
      if (!old || !Array.isArray(old.items)) return old;
      return {
        ...old,
        items: old.items.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
      };
    },
  );
  queryClient.setQueryData(queryKeys.admin.users.detail(updated.id), updated);
}

export function useAdminUsersList(params: AdminUserListParams) {
  return useQuery<AdminUserListResponse>({
    queryKey: queryKeys.admin.users.list(params as Record<string, unknown>),
    queryFn: () => listAdminUsers(params),
    placeholderData: keepPreviousData,
  });
}

export function useAdminUser(id: string | null | undefined) {
  return useQuery<AdminUserDetail>({
    queryKey: queryKeys.admin.users.detail(id ?? ''),
    queryFn: () => getAdminUser(id as string),
    enabled: !!id,
  });
}

function useInvalidateUsers() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.admin.users.all });
}

export function useInviteAdminUser() {
  const invalidate = useInvalidateUsers();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: (payload: AdminUserInvitePayload) => inviteAdminUser(payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Convite enviado por email.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useUpdateAdminUser(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateUsers();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: (payload: AdminUserUpdatePayload) => updateAdminUser(id, payload),
    onSuccess: (updated) => {
      patchListsWithUpdatedUser(queryClient, updated);
      invalidate();
      showSuccess('Usuário atualizado.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useToggleAdminUserBlocked(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateUsers();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: (blocked: boolean) => setAdminUserBlocked(id, blocked),
    onSuccess: (updated, blocked) => {
      patchListsWithUpdatedUser(queryClient, updated);
      invalidate();
      showSuccess(blocked ? 'Usuário bloqueado.' : 'Usuário desbloqueado.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useResendAdminInvite(id: string) {
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: () => resendAdminInvite(id),
    onSuccess: () => showSuccess('Convite reenviado por email.'),
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useForceAdminUserEmailVerified(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateUsers();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: () => forceAdminUserEmailVerified(id),
    onSuccess: (updated) => {
      patchListsWithUpdatedUser(queryClient, updated);
      invalidate();
      showSuccess('E-mail verificado com sucesso.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}

export function useDeleteAdminUser(id: string) {
  const invalidate = useInvalidateUsers();
  const { showSuccess, showError } = useNotification();
  return useMutation({
    mutationFn: () => deleteAdminUser(id),
    onSuccess: () => {
      invalidate();
      showSuccess('Usuário excluído.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });
}
